import { useMemo } from 'react';
import { Box, Button, useTheme } from '@mui/material';
import { CampaignFilterCategory } from '@prisma/client';
import CloseRounded from '@mui/icons-material/CloseRounded';
import SingleSelectControlled from '../../../form/base/SingleSelectControlled';
import { CampaignFilter } from '../../../../types/marketing';
import {
  AppointmentServiceInput,
  CountInputs,
  DateInputs,
  EmployeeSelectInput,
  FlashBookingInput,
  inputContainerStyle,
  SpendInputs,
  TattooDescriptionChips,
  TattooDescriptionInput,
  VisitedOnInput,
} from './inputs';
import useUser from '../../../../hooks/global/useUser';
import { getAccessLevel } from '../../../../helpers/marketing';

interface Props {
  filter: CampaignFilter;
  onChange: (filter: Partial<CampaignFilter>) => void;
  onDelete?: () => void;
}

function SegmentFilter({ filter, onChange, onDelete }: Props) {
  const theme = useTheme();
  const { user } = useUser();

  const accessLevel = getAccessLevel(user);

  const category =
    filter.category === 'LAST_APPOINTMENT_DATE'
      ? 'LAST_APPOINTMENT_WITHIN_DAYS'
      : filter.category;

  const options = useMemo(() => {
    const initialOptions = [
      { value: 'SPEND', label: 'Total spend' },
      {
        value: 'LAST_APPOINTMENT_WITHIN_DAYS',
        label: 'Last Visit',
      },
      {
        value: 'LAST_APPOINTMENT_DATE',
        label: 'Last Visit',
        hidden: true,
      },
      { value: 'APPOINTMENT_COUNT', label: 'Visit Count' },
      { value: 'APPOINTMENT_SERVICE', label: 'Service History' },
      { value: 'VISITED_ON', label: 'Visited On' },
      { value: 'FLASH_BOOKING', label: 'Flash Booking' },
      { value: 'TATTOO_DESCRIPTION', label: 'Tattoo Description' },
    ];

    if (user?.business?.type === 'STUDIO' && accessLevel === 'full') {
      initialOptions.push({
        value: 'EMPLOYEE_RELATION',
        label: 'Service Provider',
      });
    }

    return initialOptions;
  }, [user?.business?.type, accessLevel]);

  return (
    <Box
      overflow="auto"
      bgcolor={theme.palette.lightGrey.main}
      borderRadius="20px"
      py={2}
      px={2.5}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box {...inputContainerStyle}>
        <SingleSelectControlled
          id="category"
          placeholder="Select a filter"
          options={options}
          onChange={value => {
            const arrayValueCategories = [
              'FLASH_BOOKING',
              'TATTOO_DESCRIPTION',
            ];
            const initialValue = arrayValueCategories.includes(value) ? [] : '';
            onChange({
              category: value as CampaignFilterCategory,
              value: initialValue as any,
              operator: 'GT',
            });
          }}
          value={category}
          autoWidth={false}
        />

        {category === 'SPEND' && (
          <SpendInputs filter={filter} onChange={onChange} />
        )}
        {category === 'LAST_APPOINTMENT_WITHIN_DAYS' && (
          <DateInputs filter={filter} onChange={onChange} />
        )}
        {category === 'APPOINTMENT_COUNT' && (
          <CountInputs filter={filter} onChange={onChange} />
        )}
        {category === 'EMPLOYEE_RELATION' && accessLevel === 'full' && (
          <EmployeeSelectInput filter={filter} onChange={onChange} />
        )}
        {category === 'APPOINTMENT_SERVICE' && (
          <AppointmentServiceInput filter={filter} onChange={onChange} />
        )}
        {category === 'VISITED_ON' && (
          <VisitedOnInput filter={filter} onChange={onChange} />
        )}
        {category === 'FLASH_BOOKING' && (
          <FlashBookingInput filter={filter} onChange={onChange} />
        )}
        {category === 'TATTOO_DESCRIPTION' && (
          <TattooDescriptionInput filter={filter} onChange={onChange} />
        )}

        {onDelete && (
          <Button
            variant="text"
            sx={{
              minWidth: 0,
              ml: 'auto',
              aspectRatio: '1/1',
            }}
            onClick={onDelete}>
            <CloseRounded
              sx={{
                width: '16px',
                height: '16px',
              }}
              htmlColor={theme.palette.darkGrey.main}
            />
          </Button>
        )}
      </Box>

      {category === 'TATTOO_DESCRIPTION' && (
        <TattooDescriptionChips filter={filter} onChange={onChange} />
      )}
    </Box>
  );
}

export default SegmentFilter;
